import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import styles from './quick-booking.module.scss';
import {
  ModalWindow,
  DefaultButton,
  MiniInput,
  DescriptionMiniInput,
  UniversalImage,
  CircleImage,
  showConfirmDialog,
} from 'shared/ui';
import { BookingApi } from 'shared/api';
import { businessStore } from 'entities/index';
import {
  StepService,
  StepEmployee,
  StepTime,
} from 'features/mini-booking/ui/steps';
import { MiniCalendar } from 'features/mini-calendar/ui/mini-calendar';
import { ICalendarDate } from 'entities/calendar/model/calendar-date';
import { IServicesCard } from 'shared/api/booking/interfaces/services-list.interface';
import * as dateFns from 'date-fns';
import { BookingTypeEnum } from 'shared/api/booking/interfaces/booking-type.enum';
import { ICalendarBookingType } from 'shared/api/booking/interfaces/calendar-booking-type.interface';
import { ICalendarDayWorkDay } from 'shared/api/booking/interfaces/calendar-day.type';

interface ITimeSlot {
  startedAt: string;
  finishedAt: string;
}

interface IQuickBookingProps {
  startTime: string;
  employeeId: string;
  date: Date;
  onClose: () => void;
  onCreated: () => void;
  isEdit?: boolean;
  bookingData?: ICalendarBookingType;
  existingBookings?: ITimeSlot[];
  existingBreaks?: ITimeSlot[];
  workDay?: ICalendarDayWorkDay;
}

export const QuickBooking: React.FC<IQuickBookingProps> = observer(
  ({
    startTime,
    employeeId,
    date,
    onClose,
    onCreated,
    isEdit = false,
    bookingData,
    existingBookings = [],
    existingBreaks = [],
    workDay,
  }) => {
    const [selectedServiceId, setSelectedServiceId] = useState<string>('');
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(employeeId);
    const [selectedTime, setSelectedTime] = useState(startTime);
    const [selectedDate, setSelectedDate] = useState<Date>(date);
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [comment, setComment] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [view, setView] = useState<
      'main' | 'service' | 'employee' | 'date' | 'time'
    >('main');
    const [unavailableEmployeeIds, setUnavailableEmployeeIds] = useState<
      string[]
    >([]);

    const pointId = businessStore.pointId;

    useEffect(() => {
      setSelectedDate(date);
    }, [date]);

    useEffect(() => {
      if (isEdit && bookingData) {
        setCustomerName(bookingData.customer.name || '');
        setCustomerPhone(bookingData.customer.phone || '');
        setComment(bookingData.comment || '');
        setSelectedTime(bookingData.time.startedAt || startTime);

        const loadServiceByName = async () => {
          if (!pointId) return;
          try {
            const services = await BookingApi.getServiceList({ pointId });
            const service = services.find(
              (s) => s.name === bookingData.serviceName
            );
            if (service) {
              setSelectedServiceId(service.id);
            }
          } catch {
            // service not found by name, ignore — name may have changed
          }
        };
        loadServiceByName();
      }
    }, [isEdit, bookingData, startTime]);

    useEffect(() => {
      if (view === 'employee' && pointId) {
        BookingApi.getCalendarDay({
          pointId,
          date: selectedDate,
        })
          .then((data) => {
            const ids = data
              .filter(
                (e) =>
                  !e.workDay ||
                  !e.workDay.startedAt ||
                  !e.workDay.finishedAt ||
                  e.workDay.type === 'dayOff'
              )
              .map((e) => e.employee.id);
            setUnavailableEmployeeIds(ids);
          })
          .catch(() => setUnavailableEmployeeIds([]));
      }
    }, [view, pointId, selectedDate]);

    const handleServiceSelect = (serviceId: string) => {
      setSelectedServiceId(serviceId || '');
    };

    const handleDateSelect = (calendarDate: ICalendarDate) => {
      const newDate = new Date(
        calendarDate.year,
        calendarDate.month,
        calendarDate.day
      );
      setSelectedDate(newDate);
    };

    const selectedService: IServicesCard | undefined =
      businessStore.servicesList?.find((s) => s.id === selectedServiceId);

    const selectedServiceComp = selectedService ? (
      <div
        className={`${styles.selected__card} ${styles.service_container__card__selected}`}
      >
        <div className={styles.selected__card_data}>
          <div className={styles.selected__card_img}>
            <UniversalImage imgSrc={selectedService.imgUrl} />
          </div>
          <div className={styles.selected__card_line}></div>
          <div className={styles.selected__card_info}>
            <div className={styles.selected__info_name}>
              {selectedService.name}
            </div>
            <div className={styles.selected__info_data}>
              <span>{selectedService.price} ₽</span>
              <span className={styles.selected__info_duration}>
                {selectedService.duration} мин.
              </span>
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div className={styles.placeholder}>Выберите услугу</div>
    );

    const selectedEmployee = businessStore.employeesWidgetData?.find(
      (e) => e.id === selectedEmployeeId
    );
    const selectedEmployeeComp = selectedEmployee ? (
      <div
        className={`${styles.selected__card} ${styles.service_container__card__selected}`}
      >
        <div className={styles.selected__card_data}>
          <div className={styles.selected__card_img}>
            <CircleImage imgSrc={selectedEmployee.imgUrl} imgSize={34} />
          </div>
          <div className={styles.selected__card_line}></div>
          <div className={styles.selected__card_info}>
            <div className={styles.selected__info_name}>
              {selectedEmployee.fio}
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div className={styles.placeholder}>Выберите сотрудника</div>
    );

    const addMinutes = (time: string, minutes: number): string => {
      const [h, m] = time.split(':').map(Number);
      const total = h * 60 + m + minutes;
      const hh = Math.floor(total / 60) % 24;
      const mm = total % 60;
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
    };

    const endTime =
      selectedTime && selectedService
        ? addMinutes(selectedTime, selectedService.duration)
        : null;

    const selectedDateComp = (
      <div className={styles.selected__date}>
        {dateFns.format(selectedDate, 'dd.MM.yyyy')}
      </div>
    );

    const selectedTimeComp = selectedTime ? (
      <div className={styles.selected__time}>
        {selectedTime}
        {endTime && <span> — {endTime}</span>}
      </div>
    ) : (
      <div className={styles.placeholder}>Выберите время</div>
    );

    const timeToMinutes = (time: string) => {
      const [h, m] = time.split(':').map(Number);
      return h * 60 + m;
    };

    const handleCreate = async () => {
      if (!pointId) {
        setError('Point ID not found');
        return;
      }

      if (!customerName.trim()) {
        setError('Введите имя клиента');
        return;
      }

      if (
        !customerPhone.trim() ||
        customerPhone.replace(/\D/g, '').length < 11
      ) {
        setError('Введите корректный номер телефона');
        return;
      }

      if (!selectedServiceId) {
        setError('Выберите услугу');
        return;
      }

      if (!selectedService) {
        setError('Выберите услугу');
        return;
      }

      try {
        const dayInfo = await BookingApi.getCalendarDay({
          pointId,
          date: selectedDate,
        });
        const empSchedule = dayInfo.find(
          (e) => e.employee.id === selectedEmployeeId
        );
        if (
          !empSchedule?.workDay ||
          !empSchedule.workDay.startedAt ||
          !empSchedule.workDay.finishedAt ||
          empSchedule.workDay.type === 'dayOff'
        ) {
          setError('Сотрудник не работает в выбранный день');
          setIsLoading(false);
          return;
        }
      } catch {
        setError('Ошибка при проверке рабочего дня сотрудника');
        setIsLoading(false);
        return;
      }

      const newStart = timeToMinutes(selectedTime);
      const newEnd = timeToMinutes(
        addMinutes(selectedTime, selectedService.duration)
      );

      if (workDay?.startedAt && workDay?.finishedAt && !isEdit) {
        const wdStart = timeToMinutes(workDay.startedAt);
        const wdEnd = timeToMinutes(workDay.finishedAt);
        if (newStart < wdStart || newEnd > wdEnd) {
          const confirmed = await showConfirmDialog(
            `Время записи (${selectedTime}–${endTime}) выходит за рамки рабочего графика сотрудника (${workDay.startedAt}–${workDay.finishedAt}). Всё равно создать?`
          );
          if (!confirmed) return;
        }
      }

      const overlappingItems: string[] = [];

      if (existingBookings && !isEdit) {
        for (const b of existingBookings) {
          const bStart = timeToMinutes(b.startedAt);
          const bEnd = timeToMinutes(b.finishedAt);
          if (newStart < bEnd && newEnd > bStart) {
            overlappingItems.push(
              `Запись клиента ${b.startedAt}–${b.finishedAt}`
            );
          }
        }
      }

      if (existingBreaks) {
        for (const b of existingBreaks) {
          const bStart = timeToMinutes(b.startedAt);
          const bEnd = timeToMinutes(b.finishedAt);
          if (newStart < bEnd && newEnd > bStart) {
            overlappingItems.push(`Перерыв ${b.startedAt}–${b.finishedAt}`);
          }
        }
      }

      if (overlappingItems.length > 0) {
        const confirmed = await showConfirmDialog(
          `Новая запись (${selectedTime}–${addMinutes(selectedTime, selectedService.duration)}) пересекается с: ${overlappingItems.join(', ')}. Всё равно создать?`
        );
        if (!confirmed) return;
      }

      setIsLoading(true);
      setError(null);

      let success = false;

      try {
        const [hours, minutes] = selectedTime.split(':');
        const dateWithTime = new Date(selectedDate);
        dateWithTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        const startAt = dateFns.format(
          dateWithTime,
          "yyyy-MM-dd'T'HH:mm:ssxxx"
        );

        if (isEdit && bookingData) {
          await BookingApi.updateBooking({
            bookingId: bookingData.id,
            pointId,
            customerPhone: customerPhone || undefined,
            customerName: customerName || undefined,
            comment: comment || undefined,
            employeeId: employeeId || undefined,
            serviceId: selectedServiceId || undefined,
            startAt,
            type: BookingTypeEnum.MANAGER_CREATED,
          });
        } else {
          await BookingApi.createBooking({
            pointId,
            customerPhone: customerPhone || undefined,
            customerName: customerName || undefined,
            comment: comment || undefined,
            employeeId,
            serviceId: selectedServiceId,
            startAt,
            type: BookingTypeEnum.MANAGER_CREATED,
          });
        }

        success = true;
        onCreated();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : isEdit
              ? 'Ошибка при сохранении записи'
              : 'Ошибка при создании записи'
        );
      } finally {
        if (!success) {
          setIsLoading(false);
        }
      }
    };

    const renderMainView = () => (
      <>
        <div className={styles.title}>
          {isEdit ? 'Редактирование записи' : 'Создание записи'}
        </div>

        <div
          className={styles.clickableField}
          onClick={() => setView('service')}
        >
          <div className={styles.fieldHeader}>
            <span className={styles.fieldTitle}>Услуга</span>
            <span className={styles.fieldArrow}>›</span>
          </div>
          {selectedServiceComp}
        </div>

        <div
          className={styles.clickableField}
          onClick={() => setView('employee')}
        >
          <div className={styles.fieldHeader}>
            <span className={styles.fieldTitle}>Сотрудник</span>
            <span className={styles.fieldArrow}>›</span>
          </div>
          {selectedEmployeeComp}
        </div>

        <div className={styles.clickableField} onClick={() => setView('date')}>
          <div className={styles.fieldHeader}>
            <span className={styles.fieldTitle}>Дата</span>
            <span className={styles.fieldArrow}>›</span>
          </div>
          {selectedDateComp}
        </div>

        <div className={styles.clickableField} onClick={() => setView('time')}>
          <div className={styles.fieldHeader}>
            <span className={styles.fieldTitle}>Время</span>
            <span className={styles.fieldArrow}>›</span>
          </div>
          {selectedTimeComp}
        </div>

        <div className={styles.field}>
          <MiniInput
            inputValue={customerName}
            inputTitle="Имя клиента"
            inputPlaceholder="Имя клиента"
            onChange={setCustomerName}
            style={{ padding: '8px' }}
          />
        </div>

        <div className={styles.field}>
          <MiniInput
            inputValue={customerPhone}
            inputTitle="Телефон"
            inputPlaceholder="Номер телефона"
            inputType="text"
            isPhone={true}
            onChange={(value) =>
              setCustomerPhone(value.match(/\d+/g)?.join('') || '')
            }
            style={{ padding: '8px' }}
          />
        </div>

        <div className={styles.field}>
          <DescriptionMiniInput
            inputValue={comment}
            inputTitle="Комментарий"
            inputPlaceholder="Комментарий (необязательно)"
            onChange={setComment}
          />
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actionButtons}>
          <DefaultButton
            buttonText="Отменить"
            onClick={onClose}
            className={styles.button_action}
            color="white"
          />
          <DefaultButton
            className={styles.button_action}
            buttonText={
              isLoading
                ? isEdit
                  ? 'Сохранение...'
                  : 'Создание...'
                : isEdit
                  ? 'Сохранить'
                  : 'Создать'
            }
            onClick={handleCreate}
            disabled={isLoading}
            color="green"
          />
        </div>
      </>
    );

    const renderServiceView = () => (
      <>
        <div className={styles.subHeader}>
          <button className={styles.backButton} onClick={() => setView('main')}>
            ← Назад
          </button>
          <span className={styles.subTitle}>Выбор услуги</span>
        </div>
        <StepService
          selectedServiceId={selectedServiceId}
          employeeId={selectedEmployeeId}
          onSelect={(id) => {
            handleServiceSelect(id);
            setView('main');
          }}
        />
      </>
    );

    const renderEmployeeView = () => (
      <>
        <div className={styles.subHeader}>
          <button className={styles.backButton} onClick={() => setView('main')}>
            ← Назад
          </button>
          <span className={styles.subTitle}>Выбор сотрудника</span>
        </div>
        <StepEmployee
          selectedEmployeeId={selectedEmployeeId}
          unavailableEmployeeIds={unavailableEmployeeIds}
          onSelect={(id) => {
            setSelectedEmployeeId(id);
            setView('main');
          }}
        />
      </>
    );

    const renderDateView = () => (
      <>
        <div className={styles.subHeader}>
          <button className={styles.backButton} onClick={() => setView('main')}>
            ← Назад
          </button>
          <span className={styles.subTitle}>Выбор даты</span>
        </div>
        <MiniCalendar
          selectedDateHandler={(calendarDate) => {
            handleDateSelect(calendarDate);
            setView('main');
          }}
          shouldNavigate={false}
          compact
          hideAdjacentMonths
          className={styles.quickBookingCalendar}
        />
      </>
    );

    const renderTimeView = () => (
      <>
        <div className={styles.subHeader}>
          <button className={styles.backButton} onClick={() => setView('main')}>
            ← Назад
          </button>
          <span className={styles.subTitle}>Выбор времени</span>
        </div>
        <StepTime
          employeeId={selectedEmployeeId}
          serviceId={selectedServiceId || undefined}
          date={selectedDate}
          selectedTime={selectedTime}
          onSelect={(time) => {
            setSelectedTime(time);
            setView('main');
          }}
        />
      </>
    );

    return (
      <ModalWindow onClickOutside={onClose}>
        <div className={styles.modalContent}>
          {view === 'main' && renderMainView()}
          {view === 'service' && renderServiceView()}
          {view === 'employee' && renderEmployeeView()}
          {view === 'date' && renderDateView()}
          {view === 'time' && renderTimeView()}
        </div>
      </ModalWindow>
    );
  }
);
